import React, { useEffect, useState, useRef, useMemo, Suspense } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import * as THREE from 'three';
import '../styles/diceAnimation.css';

const CYCLES = 8;
const CYCLE_INTERVAL_MS = 50;
const FINAL_DISPLAY_MS = 1700;
export const ANIM_TOTAL_MS = CYCLES * CYCLE_INTERVAL_MS + 1200 + FINAL_DISPLAY_MS;

const BASE_GEOMETRIES = {
  4:  () => new THREE.TetrahedronGeometry(1.1),
  6:  () => new THREE.BoxGeometry(1.5, 1.5, 1.5),
  8:  () => new THREE.OctahedronGeometry(1.0),
  12: () => new THREE.DodecahedronGeometry(1.0),
  20: () => new THREE.IcosahedronGeometry(1.1),
};


const FACE_TRIS = { 4: 1, 6: 2, 8: 1, 10: 2, 12: 3, 20: 1 };

function addPastelRainbowColors(geo, diceType) {
  const trisPerFace = FACE_TRIS[diceType] ?? 1;
  const totalTris = geo.attributes.position.count / 3;
  const numFaces = Math.round(totalTris / trisPerFace);
  const colors = new Float32Array(totalTris * 9);
  for (let f = 0; f < numFaces; f++) {
    const c = new THREE.Color().setHSL(f / numFaces, 0.6, 0.62);
    for (let t = 0; t < trisPerFace; t++) {
      const base = (f * trisPerFace + t) * 9;
      for (let v = 0; v < 3; v++) {
        colors[base + v * 3] = c.r;
        colors[base + v * 3 + 1] = c.g;
        colors[base + v * 3 + 2] = c.b;
      }
    }
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
}

// Géométrie pour le mesh : non-indexée pour que flatShading fonctionne par face
function buildDieGeometry(type) {
  if (type === 10) return buildD10Geometry();
  const base = BASE_GEOMETRIES[type]?.() ?? new THREE.IcosahedronGeometry(1.0);
  return base.toNonIndexed();
}

// Géométrie pour les arêtes : indexée d'origine, évite les faux positifs flottants
function buildEdgeSourceGeometry(type) {
  if (type === 10) return null; // D10 gère ses arêtes manuellement
  return BASE_GEOMETRIES[type]?.() ?? new THREE.IcosahedronGeometry(1.0);
}

// Trapézoèdre pentagonal — paramètres issus de la référence (height=1.5, offset=0.5, radius=1)
function d10Vertices() {
  const radius = 1, height = 1.1, offset = 0.4;
  const top = new THREE.Vector3(0, height, 0);
  const bot = new THREE.Vector3(0, -height, 0);
  const upper = Array.from({ length: 5 }, (_, i) => {
    const a = (i * Math.PI * 2) / 5;
    return new THREE.Vector3(Math.cos(a) * radius, offset, Math.sin(a) * radius);
  });
  const lower = Array.from({ length: 5 }, (_, i) => {
    const a = (i * Math.PI * 2) / 5 + Math.PI / 5;
    return new THREE.Vector3(Math.cos(a) * radius, -offset, Math.sin(a) * radius);
  });
  return { top, bot, upper, lower };
}

function buildD10Geometry() {
  const { top, bot, upper, lower } = d10Vertices();
  const positions = [];
  const push = (v) => positions.push(v.x, v.y, v.z);
  for (let i = 0; i < 5; i++) {
    const j = (i + 1) % 5;
    // Kite haut i — winding outward vérifié : top,upper[j],upper[i] et upper[i],upper[j],lower[i]
    push(top);      push(upper[j]); push(upper[i]);
    push(upper[i]); push(upper[j]); push(lower[i]);
    // Kite bas i — winding outward vérifié : bot,lower[i],lower[j] et lower[j],lower[i],upper[j]
    push(bot);      push(lower[i]); push(lower[j]);
    push(lower[j]); push(lower[i]); push(upper[j]);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
  geo.computeVertexNormals();
  return geo;
}

function buildEdgeGeometry(diceType) {
  if (diceType === 10) return buildD10EdgeGeometry();
  const srcGeo = buildEdgeSourceGeometry(diceType);
  // Seuil à 25° : élimine les diagonales internes coplanaires sans rater de vraies arêtes
  return new THREE.EdgesGeometry(srcGeo, 25);
}

function buildD10EdgeGeometry() {
  const { top, bot, upper, lower } = d10Vertices();
  const positions = [];
  const seg = (a, b) => positions.push(a.x, a.y, a.z, b.x, b.y, b.z);
  for (let i = 0; i < 5; i++) {
    const j = (i + 1) % 5;
    seg(top, upper[i]);       // top → upper[i]
    seg(bot, lower[i]);       // bot → lower[i]
    seg(upper[i], lower[i]);  // upper[i] → lower[i]
    seg(lower[i], upper[j]);  // lower[i] → upper[i+1]
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
  return geo;
}

function computeD10RestTransform() {
  const { top, bot, upper, lower } = d10Vertices();
  const zAxis = new THREE.Vector3(0, 0, 1);
  const ab = new THREE.Vector3(), ac = new THREE.Vector3();
  const n1 = new THREE.Vector3(), n2 = new THREE.Vector3();

  let bestDot = -Infinity, bestNormal = new THREE.Vector3(0, 0, 1), bestCenter = new THREE.Vector3();

  const avgNormal = (t1a, t1b, t1c, t2a, t2b, t2c) => {
    ab.subVectors(t1b, t1a); ac.subVectors(t1c, t1a);
    n1.crossVectors(ab, ac).normalize();
    ab.subVectors(t2b, t2a); ac.subVectors(t2c, t2a);
    n2.crossVectors(ab, ac).normalize();
    return n1.clone().add(n2).normalize();
  };

  for (let i = 0; i < 5; i++) {
    const j = (i + 1) % 5;
    // winding identique à buildD10Geometry
    const nTop = avgNormal(top, upper[j], upper[i], upper[i], upper[j], lower[i]);
    const cTop = top.clone().add(upper[i]).add(upper[j]).add(lower[i]).multiplyScalar(0.25);
    if (nTop.dot(zAxis) > bestDot) { bestDot = nTop.dot(zAxis); bestNormal.copy(nTop); bestCenter.copy(cTop); }

    const nBot = avgNormal(bot, lower[i], lower[j], lower[j], lower[i], upper[j]);
    const cBot = bot.clone().add(lower[i]).add(lower[j]).add(upper[j]).multiplyScalar(0.25);
    if (nBot.dot(zAxis) > bestDot) { bestDot = nBot.dot(zAxis); bestNormal.copy(nBot); bestCenter.copy(cBot); }
  }

  const quaternion = new THREE.Quaternion().setFromUnitVectors(bestNormal, zAxis);
  return { quaternion, center: bestCenter };
}

// Trouve la face la plus alignée avec +z.
// Regroupe tous les triangles ayant la même normale (même face polygonale)
// pour calculer le vrai centre de la face, pas juste d'un triangle.
function computeRestTransform(geo, diceType) {
  if (diceType === 10) return computeD10RestTransform();

  const pos = geo.attributes.position;
  const idx = geo.index;
  const zAxis = new THREE.Vector3(0, 0, 1);
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
  const ab = new THREE.Vector3(), ac = new THREE.Vector3(), n = new THREE.Vector3();

  // Collecte toutes les faces (normale + centroïde)
  const faces = [];
  const sample = (i0, i1, i2) => {
    a.fromBufferAttribute(pos, i0);
    b.fromBufferAttribute(pos, i1);
    c.fromBufferAttribute(pos, i2);
    ab.subVectors(b, a); ac.subVectors(c, a);
    n.crossVectors(ab, ac).normalize();
    faces.push({ nx: n.x, ny: n.y, nz: n.z, cx: (a.x+b.x+c.x)/3, cy: (a.y+b.y+c.y)/3, cz: (a.z+b.z+c.z)/3 });
  };
  if (idx) {
    for (let i = 0; i < idx.count; i += 3) sample(idx.getX(i), idx.getX(i+1), idx.getX(i+2));
  } else {
    for (let i = 0; i < pos.count; i += 3) sample(i, i+1, i+2);
  }

  // Trouve le groupe de triangles co-planaires le plus aligné avec +z
  const EPS = 0.01;
  let bestDot = -Infinity, bestNormal = new THREE.Vector3(0,0,1), bestCenter = new THREE.Vector3();
  faces.forEach(f => {
    const dot = f.nx*0 + f.ny*0 + f.nz*1; // dot with zAxis
    if (dot <= bestDot) return;
    // Regroupe tous les triangles avec la même normale
    const group = faces.filter(g =>
      Math.abs(g.nx - f.nx) < EPS && Math.abs(g.ny - f.ny) < EPS && Math.abs(g.nz - f.nz) < EPS
    );
    const cx = group.reduce((s, g) => s + g.cx, 0) / group.length;
    const cy = group.reduce((s, g) => s + g.cy, 0) / group.length;
    const cz = group.reduce((s, g) => s + g.cz, 0) / group.length;
    bestDot = dot;
    bestNormal.set(f.nx, f.ny, f.nz);
    bestCenter.set(cx, cy, cz);
  });

  const quaternion = new THREE.Quaternion().setFromUnitVectors(bestNormal, zAxis);
  return { quaternion, center: bestCenter };
}

// Taille du texte par type de dé (calibrée pour tenir dans la face)
const DIE_FONT_SIZE = {
  4: 0.32, 6: 0.58, 8: 0.38, 10: 0.48, 12: 0.40, 20: 0.34,
};

function Die({ diceType, spinning, onAnimDone, offsetX = 0, color = '#ffad23' }) {
  const groupRef = useRef();
  const doneRef = useRef(false);
  const isPastel = color === 'rainbow-pastel';
  const geo = useMemo(() => {
    const g = buildDieGeometry(diceType);
    if (isPastel) addPastelRainbowColors(g, diceType);
    return g;
  }, [diceType, isPastel]);
  const edgeGeo = useMemo(() => buildEdgeGeometry(diceType), [diceType]);
  const { quaternion: restQuat } = useMemo(() => computeRestTransform(geo, diceType), [geo, diceType]);

  useFrame((_, delta) => {
    if (!groupRef.current) return;
    const g = groupRef.current;

    if (spinning) {
      doneRef.current = false;
      g.rotation.x += delta * 14;
      g.rotation.y += delta * 20;
      g.rotation.z += delta * 9;
    } else if (!doneRef.current) {
      doneRef.current = true;
      g.quaternion.copy(restQuat);
      onAnimDone && onAnimDone();
    }
  });

  return (
    <group ref={groupRef} position={[offsetX, 0, 0]}>
      <mesh geometry={geo}>
        <meshStandardMaterial vertexColors={isPastel} color={isPastel ? undefined : color} flatShading roughness={0.5} metalness={0.1} />
      </mesh>
      <lineSegments geometry={edgeGeo}>
        <lineBasicMaterial color="#ffffff" polygonOffset polygonOffsetFactor={-1} polygonOffsetUnits={-1} />
      </lineSegments>
    </group>
  );
}

// Texte en coordonnées monde, toujours face caméra (pas enfant du groupe rotatif)
function DieLabel({ diceType, offsetX, value, visible }) {
  const geo = useMemo(() => buildDieGeometry(diceType), [diceType]);
  const { quaternion: restQuat, center: faceCenter } = useMemo(() => computeRestTransform(geo, diceType), [geo, diceType]);
  const fontSize = DIE_FONT_SIZE[diceType] || 0.38;

  // Position monde = faceCenter tourné par restQuat + offsetX du groupe
  const worldPos = useMemo(() => {
    const p = faceCenter.clone().applyQuaternion(restQuat);
    p.x += offsetX;
    p.z += diceType === 10 ? 0.25 : 0.1;
    return [p.x, p.y, p.z];
  }, [faceCenter, restQuat, offsetX]);

  if (!visible || value === null) return null;

  return (
    <Text
      position={worldPos}
      fontSize={fontSize}
      color="#ffffff"
      fontWeight="bold"
      anchorX="center"
      anchorY="middle"
      outlineWidth={0.01}
      outlineColor="#000000"
    >
      {String(value)}
    </Text>
  );
}

const DiceAnimationOverlay = ({ diceType, finalValue, onClose, debug = false, diceColor = '#ffad23' }) => {
  const resolvedColor = useMemo(() =>
    diceColor === 'rainbow' ? 'rainbow-pastel' : diceColor
  , [diceColor]);
  const [isFinal, setIsFinal] = useState(debug);
  const [spinning, setSpinning] = useState(!debug);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const isD100 = diceType === 100;

  useEffect(() => {
    if (debug) return;
    let count = 0;
    const interval = setInterval(() => {
      if (count < CYCLES) {
        count++;
      } else {
        clearInterval(interval);
        setSpinning(false);
      }
    }, CYCLE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [diceType, finalValue, debug]);

  const handleAnimDone = () => {
    setIsFinal(true);
    if (!debug) setTimeout(() => onCloseRef.current(), FINAL_DISPLAY_MS);
  };

  const die3dType = isD100 ? 10 : diceType;

  // Pour D100 : afficher les dizaines sur le premier dé, les unités sur le second
  const rawTens = isD100 ? Math.floor((finalValue - 1) / 10) * 10 : null;
  const tensValue = rawTens === 0 ? '00' : rawTens;
  const unitsValue = isD100 ? (finalValue - 1) % 10 + 1 : null;

  return (
    <div className="diceAnimOverlay" onClick={isFinal ? () => onCloseRef.current() : undefined}>
      <Canvas
        camera={{ position: [0, 0, 10], fov: 50 }}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
      >
        <ambientLight intensity={0.1} />
        <directionalLight position={[0, 0, 10]} intensity={0.9} />
        <directionalLight position={[-5, -5, -5]} intensity={0.05} />
        <Suspense fallback={null}>
          <Die
            diceType={die3dType}
            spinning={spinning}
            onAnimDone={handleAnimDone}
            offsetX={isD100 ? -1.2 : 0}
            color={resolvedColor}
          />
          {isD100 && (
            <Die
              diceType={10}
              spinning={spinning}
              onAnimDone={null}
              offsetX={1.2}
              color={resolvedColor}
            />
          )}
          <DieLabel
            diceType={die3dType}
            offsetX={isD100 ? -1.2 : 0}
            value={isD100 ? tensValue : finalValue}
            visible={isFinal}
          />
          {isD100 && (
            <DieLabel
              diceType={10}
              offsetX={1.2}
              value={unitsValue}
              visible={isFinal}
            />
          )}
        </Suspense>
      </Canvas>
    </div>
  );
};

export default DiceAnimationOverlay;
